import { _ as _extends } from '../../_rollupPluginBabelHelpers-c245b26a.js';
import React, { useRef, cloneElement, useEffect } from 'react';
import { keyframes, jsx } from '@emotion/react';
import { d as durations, e as easings } from '../../index-26fc6478.js';
import { mergeRefs } from '../../utils/mergeRefs.js';
import { useDebouncedCallback } from 'use-debounce';
import useId from '@indeed/ihl-use-id';
import transition from '../../utils/transition.js';
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect.js';
import { Popup } from '../Popup/Popup.js';
import { Portal } from '../Portal/Portal.js';
import '@floating-ui/react-dom';
import '@indeed/ifl-css';
import '../../hooks/useMergeRefs.js';
import '../Box/Box.js';
import 'react-dom';

const animFadeIn = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
  `;
const popupStyles = _ref => {
  let {
    animate
  } = _ref;
  return {
    animation: animate ? `${animFadeIn} ${durations.normal} ${easings.linear} forwards` : "none",
    paddingInlineStart: 3,
    paddingInlineEnd: 3,
    paddingBlockStart: 1,
    paddingBlockEnd: 1,
    fontFamily: "default",
    variant: "text.level2",
    transition: _ref2 => {
      let {
        transitions
      } = _ref2;
      return transition(transitions.default, "opacity");
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none"
    },
    maxInlineSize: 260,
    backgroundColor: "background.inverse",
    color: "foreground.inverse",
    border: "1px solid",
    borderColor: "transparent",
    borderRadius: "sm",
    boxShadow: "none",
    boxSizing: "content-box",
    position: "relative",
    "::after": {
      position: "absolute",
      top: "-13px",
      left: "-13px",
      right: "-13px",
      bottom: "-13px",
      content: '""',
      zIndex: -1,
      backgroundColor: "transparent",
      padding: 5
    },
    zIndex: 9999 // Match Tippy default z-index
  };
};

/**
 * @see https://experience.indeed.design/components/ids/tooltip
 */
const PopupTooltip = _ref3 => {
  let {
    alignment = "center",
    aria,
    children,
    visible: defaultVisible,
    delay = 100,
    label,
    onHide = () => {},
    onShow = () => {},
    popupProps = {},
    portaled = false,
    side = "block-end",
    trigger = "mouseenter focus"
  } = _ref3;
  const ariaProps = {
    content: "describedby",
    expanded: false,
    ...aria
  };

  // Set `visible` to `undefined` initially. We'll render initial visible Popup via `useIsomorphicLayoutEffect` hook below.
  const [visible, _setVisible] = React.useState(undefined);
  const shouldAnimate = useRef(false);
  const popupRef = useRef(null);
  const myRef = useRef(null);
  const popupId = `ifl-Popup-${useId()}`;
  const showDelay = Array.isArray(delay) ? delay[0] || 100 : delay;
  const debouncedSetVisible = useDebouncedCallback(value => {
    setVisible(value);
  }, showDelay);
  const setVisible = value => {
    shouldAnimate.current = true;
    let shouldUpdate = true;
    if (visible !== value) {
      if (value === true) {
        // @ts-ignore
        shouldUpdate = onShow() ?? true;
      } else {
        // @ts-ignore
        shouldUpdate = onHide() ?? true;
        if (shouldUpdate) {
          debouncedSetVisible.cancel();
        }
      }
    }

    // Only update visibility if event handlers allow and we're not in controlled mode
    if (shouldUpdate && defaultVisible === undefined) {
      _setVisible(value);
    }
  };
  const triggerMap = {
    mouseenter: {
      onMouseEnter: () => {
        debouncedSetVisible(true);
      },
      onMouseLeave: () => {
        setVisible(false);
      }
    },
    focus: {
      onFocus: () => {
        setVisible(true);
      }
    },
    click: {
      onClick: () => {
        setVisible(!visible);
      },
      onMouseDown: e => {
        e.stopPropagation();
      }
    },
    manual: {},
    touch: {
      onTouchEnd: () => {
        debouncedSetVisible(true);
      }
    }
  };
  const childProps = trigger.split(" ").map(t => triggerMap[t]).reduce((acc, ps) => {
    return {
      ...acc,
      ...ps
    };
  }, {});
  if (ariaProps.content === "labelledby" || ariaProps.content === "describedby") {
    childProps[`aria-${ariaProps.content}`] = popupId;
  }
  if (ariaProps.expanded === true) {
    childProps["aria-expanded"] = visible ?? false;
  }
  const childWithRef = children ? /*#__PURE__*/cloneElement(children, {
    // @ts-ignore
    ref: children.ref ? mergeRefs(myRef, children.ref) : myRef,
    onBlur: () => {
      setVisible(false);
    },
    ...childProps
  }) : null;

  // Render without animation if defaultVisible = true
  useIsomorphicLayoutEffect(() => {
    if (visible === undefined || defaultVisible !== visible) {
      shouldAnimate.current = false;
      _setVisible(defaultVisible);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultVisible]);

  // Listen for Escape keypresses or mouse clicks to close Tooltip
  useEffect(() => {
    const onKeyPress = event => {
      if (event.key === "Escape") {
        setVisible(false);
      }
    };
    const onMouseDown = () => {
      setVisible(false);
    };
    if (visible) {
      window.dispatchEvent(new Event("ifl:closeTooltip"));
      window.addEventListener("keydown", onKeyPress);
      window.addEventListener("mousedown", onMouseDown);
      window.addEventListener("ifl:closeTooltip", onMouseDown);
    }
    return () => {
      window.removeEventListener("keydown", onKeyPress);
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("ifl:closeTooltip", onMouseDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);
  const {
    sx = {},
    ...otherPopupProps
  } = popupProps;
  const Wrapper = portaled ? Portal : React.Fragment;
  return jsx(React.Fragment, null, childWithRef, visible === true ? jsx(Wrapper, null, jsx(Popup, _extends({
    id: popupId,
    role: "tooltip",
    onMouseMove: () => {
      setVisible(true);
    },
    onMouseLeave: () => {
      if (trigger.includes("mouseenter")) {
        setVisible(false);
      }
    },
    onMouseDown: e => {
      e.preventDefault();
      e.stopPropagation();
    },
    showArrow: true,
    alignment: alignment,
    side: side,
    anchor: myRef == null ? void 0 : myRef.current,
    ref: popupRef,
    sx: {
      ...popupStyles({
        animate: shouldAnimate.current
      }),
      ...sx
    }
  }, otherPopupProps), label)) : null);
};
PopupTooltip.displayName = "Tooltip";

export { PopupTooltip };
